'use client';

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { X, Receipt, ChevronDown, Check, Repeat, CalendarDays, Paperclip, FileText } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { DatePicker } from '../ui/date-picker';
import { cn } from '@/lib/utils';
import { z } from 'zod';
import type { ParsedReceipt } from '@/lib/parse-receipt-text';
import { SUPPORTED_CURRENCIES } from '@/lib/currencies';
import { fileToExpenseReceiptDataUrl, isPdfReceiptDataUrl } from '@/lib/expense-receipt';

interface AddExpenseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  /** Prefill form from receipt scan (mobile). Consumed on open. */
  prefillFromScan?: ParsedReceipt | null;
  onPrefillConsumed?: () => void;
}

type ExpenseCategory = 'Autres' | 'Coaching' | 'Vol' | 'Hôtel' | 'Repas' | 'Transport' | 'Outils / Abonnements poker' | 'Frais administratifs';

const EXPENSE_CATEGORY_VALUES: ExpenseCategory[] = [
  'Autres',
  'Coaching',
  'Vol',
  'Hôtel',
  'Repas',
  'Transport',
  'Outils / Abonnements poker',
  'Frais administratifs',
];

const CATEGORY_I18N_KEY: Record<
  ExpenseCategory,
  'other' | 'coaching' | 'flight' | 'hotel' | 'meals' | 'transport' | 'pokerTools' | 'administrativeFees'
> = {
  Autres: 'other',
  Coaching: 'coaching',
  Vol: 'flight',
  'Hôtel': 'hotel',
  Repas: 'meals',
  Transport: 'transport',
  'Outils / Abonnements poker': 'pokerTools',
  'Frais administratifs': 'administrativeFees',
};

export const AddExpenseModal: React.FC<AddExpenseModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  prefillFromScan,
  onPrefillConsumed,
}) => {
  const t = useTranslations('expenses');
  const [category, setCategory] = useState<ExpenseCategory>('Autres');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState<Date | undefined>(new Date());
  const [currency, setCurrency] = useState('EUR');
  const [notes, setNotes] = useState('');
  const [recurringInterval, setRecurringInterval] = useState<'monthly' | 'yearly' | null>(null);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);
  const categoryDropdownRef = useRef<HTMLDivElement>(null);
  const [userFestivals, setUserFestivals] = useState<{ id: string; pokerFestival: { name: string } | null; customName?: string | null }[]>([]);
  const [selectedFestivalId, setSelectedFestivalId] = useState<string | null>(null);
  const [receiptDataUrl, setReceiptDataUrl] = useState<string | null>(null);
  const [receiptFileName, setReceiptFileName] = useState('');
  const receiptInputRef = useRef<HTMLInputElement>(null);

  const expenseCategories = useMemo(
    () =>
      EXPENSE_CATEGORY_VALUES.map((value) => ({
        value,
        label: t(`categories.${CATEGORY_I18N_KEY[value]}`),
      })),
    [t]
  );

  const expenseSchema = useMemo(
    () =>
      z.object({
        category: z.string().min(1, t('addModal.errors.categoryRequired')),
        amount: z.string().min(1, t('addModal.errors.amountRequired')).refine(
          (val) => {
            const num = parseFloat(val);
            return !isNaN(num) && num > 0;
          },
          { message: t('addModal.errors.amountPositive') }
        ),
        date: z.date({ message: t('addModal.errors.dateRequired') }),
        currency: z.string().min(1, t('addModal.errors.currencyRequired')),
        notes: z.string().optional(),
        recurringInterval: z.enum(['monthly', 'yearly']).optional().nullable(),
        endDate: z.date().optional().nullable(),
      }),
    [t]
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target as Node)) {
        setIsCategoryDropdownOpen(false);
      }
    };

    if (isCategoryDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isCategoryDropdownOpen]);

  useEffect(() => {
    if (recurringInterval) {
      setSelectedFestivalId(null);
      setReceiptDataUrl(null);
      setReceiptFileName('');
    }
  }, [recurringInterval]);

  useEffect(() => {
    if (isOpen) {
      setCategory('Autres');
      setAmount('');
      setDate(new Date());
      setCurrency('EUR');
      setNotes('');
      setRecurringInterval(null);
      setEndDate(undefined);
      setSelectedFestivalId(null);
      setReceiptDataUrl(null);
      setReceiptFileName('');
      setError('');
      if (prefillFromScan) {
        setCategory(prefillFromScan.category as ExpenseCategory);
        setAmount(prefillFromScan.amount != null ? String(prefillFromScan.amount) : '');
        setDate(prefillFromScan.date ?? undefined);
        setCurrency(prefillFromScan.currency);
        onPrefillConsumed?.();
      }
      fetch('/api/user/festivals')
        .then((res) => res.json())
        .then((data) => setUserFestivals(data.userFestivals || []))
        .catch(() => setUserFestivals([]));
    }
    // Intentionally only run when isOpen changes; prefillFromScan is read once when modal opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      const formData = {
        category,
        amount,
        date: date!,
        currency,
        notes: notes || undefined,
        recurringInterval,
        endDate: recurringInterval ? endDate ?? null : null,
      };
      expenseSchema.parse(formData);

      if (recurringInterval && endDate && date && endDate < date) {
        setError(t('addModal.errors.endBeforeStart'));
        return;
      }

      setIsSubmitting(true);

      if (recurringInterval) {
        const dayOfMonth = date!.getDate();
        const response = await fetch('/api/expenses/recurring', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            category,
            amount: parseFloat(amount),
            currency,
            interval: recurringInterval,
            dayOfMonth,
            startDate: date!.toISOString().slice(0, 10),
            endDate: endDate ? endDate.toISOString().slice(0, 10) : null,
            notes: notes || null,
          }),
        });
        const data = await response.json();
        if (!response.ok) {
          const msg = data.details ? `${data.error}: ${data.details}` : (data.error || t('addModal.errors.addRecurringExpense'));
          throw new Error(msg);
        }
      } else {
        const response = await fetch('/api/expenses', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            category,
            amount: parseFloat(amount),
            date: date!.toISOString(),
            currency,
            notes: notes || undefined,
            userFestivalId: selectedFestivalId || null,
            receiptImageDataUrl: receiptDataUrl || undefined,
          }),
        });
        const data = await response.json();
        if (!response.ok) {
          const msg = data.details ? `${data.error}: ${data.details}` : (data.error || t('addModal.errors.addExpense'));
          throw new Error(msg);
        }
      }

      onSuccess?.();
      onClose();
    } catch (err: unknown) {
      if (err instanceof z.ZodError) {
        setError(err.issues[0]?.message || t('addModal.errors.validation'));
      } else {
        setError(err instanceof Error ? err.message : t('addModal.errors.generic'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const selectedCategory = expenseCategories.find((c) => c.value === category);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal: max height + scroll only in the form area so header/footer stay visible on small screens */}
      <div className="relative z-50 flex flex-col w-full max-w-md max-h-[90vh] mx-4 bg-white dark:bg-gray-800 rounded-lg shadow-xl">
        {/* Header - always visible */}
        <div className="flex-shrink-0 flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              {t('addExpense')}
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {t('addModal.subtitle')}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form - scrollable body */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 min-h-0 overflow-y-auto p-6 space-y-6">
          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('category')}
            </label>
            <div ref={categoryDropdownRef} className="relative">
              <button
                type="button"
                onClick={() => setIsCategoryDropdownOpen(!isCategoryDropdownOpen)}
                className={cn(
                  'w-full flex items-center justify-between px-4 py-3 rounded-lg border',
                  'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600',
                  'text-gray-900 dark:text-white',
                  'hover:bg-gray-50 dark:hover:bg-gray-600',
                  'focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-100'
                )}
              >
                <div className="flex items-center gap-3">
                  <div className="text-gold">
                    <Receipt className="w-5 h-5" />
                  </div>
                  <span className="font-medium">{selectedCategory?.label}</span>
                </div>
                <ChevronDown className={cn(
                  'w-5 h-5 text-gray-400 transition-transform',
                  isCategoryDropdownOpen && 'rotate-180'
                )} />
              </button>

              {isCategoryDropdownOpen && (
                <div className="absolute z-10 w-full mt-2 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg overflow-hidden">
                  {expenseCategories.map((cat) => (
                    <button
                      key={cat.value}
                      type="button"
                      onClick={() => {
                        setCategory(cat.value);
                        setIsCategoryDropdownOpen(false);
                        setError('');
                      }}
                      className={cn(
                        'w-full flex items-center justify-between px-4 py-3 text-left',
                        'hover:bg-gray-50 dark:hover:bg-gray-600',
                        'transition-colors',
                        category === cat.value && 'bg-gold/10'
                      )}
                    >
                      <div className="flex items-center gap-3">
                        <div className={cn(
                          'text-gold',
                          category !== cat.value && 'text-gray-400'
                        )}>
                          <Receipt className="w-5 h-5" />
                        </div>
                        <span className={cn(
                          'font-medium',
                          category === cat.value
                            ? 'text-gray-900 dark:text-white'
                            : 'text-gray-700 dark:text-gray-300'
                        )}>
                          {cat.label}
                        </span>
                      </div>
                      {category === cat.value && (
                        <Check className="w-5 h-5 text-gold" />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Amount */}
          <div>
            <label htmlFor="amount" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('amount')} ({currency})
            </label>
            <Input
              id="amount"
              type="number"
              step="0.01"
              min="0.01"
              value={amount}
              onChange={(e) => {
                setAmount(e.target.value);
                setError('');
              }}
              placeholder="100.00"
              required
              className="w-full"
            />
          </div>

          {/* Currency */}
          <div>
            <label htmlFor="currency" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('addModal.currency')}
            </label>
            <div className="relative">
              <select
                id="currency"
                value={currency}
                onChange={(e) => {
                  setCurrency(e.target.value);
                  setError('');
                }}
                className={cn(
                  'w-full px-3 py-2 rounded-lg border',
                  'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600',
                  'text-gray-900 dark:text-white',
                  'focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-100'
                )}
              >
                {SUPPORTED_CURRENCIES.map((c) => (
                  <option key={c.code} value={c.code}>{c.symbol} {c.code}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Récurrence : mensuel ou annuel */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('recurrenceLabel')}
            </label>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setRecurringInterval(recurringInterval === 'monthly' ? null : 'monthly')}
                className={cn(
                  'flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors',
                  recurringInterval === 'monthly'
                    ? 'bg-gold/10 border-gold text-gray-900 dark:text-white'
                    : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                )}
              >
                <Repeat className={cn('w-5 h-5', recurringInterval === 'monthly' ? 'text-gold' : 'text-gray-400')} />
                <span className="text-sm font-medium">{t('recurringMonthly')}</span>
              </button>
              <button
                type="button"
                onClick={() => setRecurringInterval(recurringInterval === 'yearly' ? null : 'yearly')}
                className={cn(
                  'flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors',
                  recurringInterval === 'yearly'
                    ? 'bg-gold/10 border-gold text-gray-900 dark:text-white'
                    : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                )}
              >
                <CalendarDays className={cn('w-5 h-5', recurringInterval === 'yearly' ? 'text-gold' : 'text-gray-400')} />
                <span className="text-sm font-medium">{t('recurringYearly')}</span>
              </button>
            </div>
            {recurringInterval && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                {recurringInterval === 'monthly' ? t('recurringHintMonthly') : t('recurringHintYearly')}
              </p>
            )}
          </div>

          {!recurringInterval ? (
            <div>
              <label htmlFor="festival" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('festivalOptional')}
              </label>
              <select
                id="festival"
                value={selectedFestivalId ?? ''}
                onChange={(e) => setSelectedFestivalId(e.target.value || null)}
                className={cn(
                  'w-full px-3 py-2 rounded-lg border',
                  'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600',
                  'text-gray-900 dark:text-white',
                  'focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-100'
                )}
              >
                <option value="">{t('noFestival')}</option>
                {userFestivals.map((uf) => (
                  <option key={uf.id} value={uf.id}>
                    {uf.pokerFestival?.name ?? uf.customName ?? '—'}
                  </option>
                ))}
              </select>
            </div>
          ) : null}

          {/* Date / Date de début */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {recurringInterval ? t('startDateLabel') : t('addModal.dateLabel')}
            </label>
            <DatePicker
              value={date}
              onChange={(selectedDate) => {
                setDate(selectedDate);
                setError('');
              }}
              placeholder={recurringInterval ? t('addModal.startDatePlaceholder') : t('addModal.datePlaceholder')}
              className="w-full"
            />
          </div>

          {/* Date de fin (optionnelle) - seulement si récurrent */}
          {recurringInterval && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('endDateOptional')}
              </label>
              <DatePicker
                value={endDate}
                onChange={(selectedDate) => {
                  setEndDate(selectedDate ?? undefined);
                  setError('');
                }}
                placeholder={t('addModal.endDateNoEndPlaceholder')}
                className="w-full"
              />
            </div>
          )}

          {/* Notes */}
          <div>
            <label htmlFor="notes" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('addModal.notesOptional')}
            </label>
            <textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t('addModal.notesPlaceholder')}
              rows={3}
              className={cn(
                'w-full px-3 py-2 rounded-lg border',
                'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600',
                'text-gray-900 dark:text-white',
                'focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-100',
                'resize-none'
              )}
            />
          </div>

          {!recurringInterval ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('addModal.justificatifOptional')}
              </label>
              <input
                ref={receiptInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif,application/pdf,.pdf"
                className="sr-only"
                onChange={async (event) => {
                  const file = event.target.files?.[0];
                  event.target.value = '';
                  if (!file) return;
                  const result = await fileToExpenseReceiptDataUrl(file);
                  if ('error' in result) {
                    setError(
                      result.error === 'justificatifTooLarge'
                        ? t('addModal.errors.justificatifTooLarge')
                        : t('addModal.errors.justificatifType')
                    );
                    return;
                  }
                  setReceiptDataUrl(result.dataUrl);
                  setReceiptFileName(result.fileName);
                  setError('');
                }}
              />
              <button
                type="button"
                onClick={() => receiptInputRef.current?.click()}
                className={cn(
                  'w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg border transition-colors',
                  'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600',
                  'text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-600',
                  'focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-100'
                )}
              >
                <Paperclip className="w-5 h-5 text-gold" />
                <span className="text-sm font-medium">{t('addModal.justificatif')}</span>
              </button>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                {t('addModal.justificatifHint')}
              </p>
              {receiptDataUrl ? (
                <div className="mt-3 rounded-lg border border-gray-200 dark:border-gray-600 p-3 bg-gray-50 dark:bg-gray-700/50">
                  {isPdfReceiptDataUrl(receiptDataUrl) ? (
                    <div className="flex items-center gap-2 text-gray-800 dark:text-gray-100">
                      <FileText className="w-5 h-5 text-gold shrink-0" />
                      <span className="text-sm font-medium truncate">{receiptFileName}</span>
                    </div>
                  ) : (
                    <img
                      src={receiptDataUrl}
                      alt={receiptFileName || t('addModal.justificatif')}
                      className="w-full max-h-40 object-contain rounded-md bg-white dark:bg-gray-800"
                    />
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      setReceiptDataUrl(null);
                      setReceiptFileName('');
                    }}
                    className="mt-2 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                  >
                    {t('addModal.justificatifRemove')}
                  </button>
                </div>
              ) : null}
            </div>
          ) : null}

          {/* Error message */}
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}
          </div>

          {/* Actions - always visible at bottom */}
          <div className="flex-shrink-0 flex items-center gap-3 p-6 pt-4 border-t border-gray-200 dark:border-gray-700">
            <Button
              type="button"
              onClick={onClose}
              variant="outline"
              className="flex-1"
              disabled={isSubmitting}
            >
              {t('addModal.cancel')}
            </Button>
            <Button
              type="submit"
              className="flex-1 bg-gradient-to-r from-[#1a2332] via-[#0f172a] to-[#0c1220] hover:opacity-90 text-white font-semibold"
              disabled={isSubmitting}
            >
              {isSubmitting ? t('addModal.adding') : t('addModal.add')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

